import type { CSSProperties, ReactNode } from 'react'
import { cn } from '../../lib/cn.ts'
import type { ToolKey } from './tools.tsx'

/** Tool tints (DESIGN.md section 3): small surfaces only, never page fills. */
export const TINT: Record<ToolKey, string> = {
	exercises: 'bg-tint-exercises',
	words: 'bg-tint-words',
	levels: 'bg-tint-levels',
	plans: 'bg-tint-plans',
	wheel: 'bg-tint-wheel',
}

/**
 * The tool's mark: its icon on its tint. On a tool page it carries `view-transition-name:
 * tool-mark`, and the hub tile being opened carries the same name for the length of the
 * navigation, so the mark travels from the tile into the page.
 */
export function ToolMark({
	tool,
	icon,
	shared = false,
	size = 'md',
	className,
}: {
	tool: ToolKey
	icon: ReactNode
	/** Name it for the view transition (always on a tool page; only while navigating on the hub). */
	shared?: boolean
	size?: 'sm' | 'md'
	className?: string
}) {
	return (
		<span
			aria-hidden="true"
			style={shared ? ({ viewTransitionName: 'tool-mark' } as CSSProperties) : undefined}
			className={cn(
				'grid shrink-0 place-items-center text-teal-900 [--icon-accent:var(--color-amber-500)]',
				size === 'sm'
					? 'size-9 rounded-[10px] [&_svg]:size-5'
					: 'size-11 rounded-btn [&_svg]:size-6',
				TINT[tool],
				className,
			)}
		>
			{icon}
		</span>
	)
}

const line = 'block h-[3px] rounded-full bg-ink/20'

/**
 * A miniature of what the tool makes, drawn from the shape system (geometry, not a
 * screenshot): the tile shows the output, not an icon.
 */
export function ToolPreview({ tool }: { tool: ToolKey }) {
	switch (tool) {
		case 'exercises':
			return (
				<div className="flex flex-col gap-2.5">
					<span className={cn(line, 'h-1 w-1/2 bg-ink/40')} />
					{[0.9, 0.75, 0.85].map((w, i) => (
						<span key={w} className="flex items-center gap-1.5">
							<span className="size-2 shrink-0 rounded-full border border-ink/25" />
							<span className={line} style={{ width: `${w * 60}%` }} />
							<span
								className={cn(
									'h-2 w-6 shrink-0 rounded-[3px] border-b border-teal-700/50',
									i === 1 && 'bg-amber-200 border-amber-700/40',
								)}
							/>
						</span>
					))}
				</div>
			)
		case 'words':
			return (
				<div className="grid grid-cols-[2fr_3fr] gap-x-2.5 gap-y-2">
					{[0.8, 0.6, 0.9, 0.7].map((w, i) => (
						<span key={w} className="contents">
							<span
								className={cn(line, 'h-1 bg-ink/40', i === 0 && 'bg-amber-500/80')}
								style={{ width: `${w * 100}%` }}
							/>
							<span className={line} style={{ width: `${(1.3 - w) * 100}%` }} />
						</span>
					))}
				</div>
			)
		case 'levels':
			return (
				<div className="flex items-end gap-1.5">
					{[2, 3, 4].map((n) => (
						<span
							key={n}
							className="flex flex-1 flex-col gap-1.5 rounded-[4px] bg-tint-levels p-1.5"
						>
							{Array.from({ length: n }, (_, i) => (
								<span
									key={`${n}-${i.toString()}`}
									className={line}
									style={{ width: `${95 - i * 12}%` }}
								/>
							))}
						</span>
					))}
				</div>
			)
		case 'plans':
			return (
				<div className="flex flex-col gap-2">
					{[5, 15, 10, 10].map((min, i) => (
						<span key={`${min}-${i.toString()}`} className="flex items-center gap-2">
							<span className="h-2.5 w-5 shrink-0 rounded-full bg-teal-700/15" />
							<span className={line} style={{ width: `${40 + min * 2.5}%` }} />
						</span>
					))}
				</div>
			)
		case 'wheel':
			return (
				<div className="grid place-items-center">
					<span
						className="relative size-[4.25rem] rounded-full shadow-1 transition-transform duration-(--dur-slow) ease-out group-hover:rotate-[30deg] motion-reduce:transition-none"
						style={{
							background:
								'conic-gradient(var(--color-tint-wheel) 0 60deg, var(--color-teal-100) 60deg 120deg, var(--color-amber-200) 120deg 180deg, var(--color-tint-wheel) 180deg 240deg, var(--color-teal-100) 240deg 300deg, var(--color-amber-200) 300deg 360deg)',
						}}
					>
						<span className="absolute inset-[38%] rounded-full bg-sheet shadow-1" />
					</span>
				</div>
			)
	}
}
